import type {
	API_ArchivePlatformPlanResponse,
	API_CreatePlatformPlanRequest,
	API_CreatePlatformPlanResponse,
	API_GetPlatformPlanFeatureCodesResponse,
	API_GetPlatformPlanResponse,
	API_GetPlatformPlansListRequest,
	API_GetPlatformPlansListResponse,
	API_UpdatePlatformPlanEntitlementsRequest,
	API_UpdatePlatformPlanEntitlementsResponse,
	API_UpdatePlatformPlanRequest,
	API_UpdatePlatformPlanResponse,
	PlatformFeatureCode,
	PlatformPlanDetails,
	PlatformPlanListItem,
} from "@pilatestime-admin/shared-types";
import { queryOptions, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useMutationWithToast } from "@/hooks/useMutationWithToast";
import api from "./api_client";

const PLATFORM_PLANS_KEY = "platform-plans";
const FEATURE_CODES_KEY = "platform-plans-feature-codes";

const authHeader = (token: string) => ({
	headers: { Authorization: `Bearer ${token}` },
});

export const fetchPlatformPlans = (
	token: string,
	params?: API_GetPlatformPlansListRequest,
): Promise<PlatformPlanListItem[]> =>
	api
		.get<API_GetPlatformPlansListResponse>("/platform-plans", {
			...authHeader(token),
			params,
		})
		.then((res) => res.data.data.plans);

export const fetchPlatformPlansQueryOptions = (
	token: string,
	params?: API_GetPlatformPlansListRequest,
) =>
	queryOptions({
		queryKey: [PLATFORM_PLANS_KEY, "list", params],
		queryFn: () => fetchPlatformPlans(token, params),
		staleTime: 60 * 1000,
	});

export const fetchPlatformPlanById = (
	token: string,
	planId: string,
): Promise<PlatformPlanDetails> =>
	api
		.get<API_GetPlatformPlanResponse>(
			`/platform-plans/${planId}`,
			authHeader(token),
		)
		.then((res) => res.data.data);

export const fetchPlatformPlanByIdQueryOptions = (
	token: string,
	planId: string,
) =>
	queryOptions({
		queryKey: [PLATFORM_PLANS_KEY, "detail", planId],
		queryFn: () => fetchPlatformPlanById(token, planId),
		staleTime: 60 * 1000,
	});

export const fetchPlatformPlanFeatureCodes = (
	token: string,
): Promise<PlatformFeatureCode[]> =>
	api
		.get<API_GetPlatformPlanFeatureCodesResponse>(
			"/platform-plans/feature-codes",
			authHeader(token),
		)
		.then((res) => res.data.data.featureCodes);

export const fetchPlatformPlanFeatureCodesQueryOptions = (token: string) =>
	queryOptions({
		queryKey: [FEATURE_CODES_KEY],
		queryFn: () => fetchPlatformPlanFeatureCodes(token),
		staleTime: 5 * 60 * 1000,
	});

export const createPlatformPlan = (
	token: string,
	body: API_CreatePlatformPlanRequest,
): Promise<PlatformPlanDetails> =>
	api
		.post<API_CreatePlatformPlanResponse>(
			"/platform-plans",
			body,
			authHeader(token),
		)
		.then((res) => res.data.data);

export const useCreatePlatformPlan = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	return useMutationWithToast({
		mutationFn: ({
			token,
			body,
		}: {
			token: string;
			body: API_CreatePlatformPlanRequest;
		}) => createPlatformPlan(token, body),
		toastMessages: {
			loading: t("platformPlans.toasts.creating"),
			success: t("platformPlans.toasts.createSuccess"),
			error: t("platformPlans.toasts.createError"),
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [PLATFORM_PLANS_KEY] });
		},
	});
};

export const updatePlatformPlan = (
	token: string,
	planId: string,
	body: API_UpdatePlatformPlanRequest,
): Promise<PlatformPlanDetails> =>
	api
		.put<API_UpdatePlatformPlanResponse>(
			`/platform-plans/${planId}`,
			body,
			authHeader(token),
		)
		.then((res) => res.data.data);

export const useUpdatePlatformPlan = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	return useMutationWithToast({
		mutationFn: ({
			token,
			planId,
			body,
		}: {
			token: string;
			planId: string;
			body: API_UpdatePlatformPlanRequest;
		}) => updatePlatformPlan(token, planId, body),
		toastMessages: {
			loading: t("platformPlans.toasts.updating"),
			success: t("platformPlans.toasts.updateSuccess"),
			error: t("platformPlans.toasts.updateError"),
		},
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({
				queryKey: [PLATFORM_PLANS_KEY, "detail", variables.planId],
			});
			queryClient.invalidateQueries({
				queryKey: [PLATFORM_PLANS_KEY, "list"],
			});
		},
	});
};

export const archivePlatformPlan = (
	token: string,
	planId: string,
): Promise<PlatformPlanDetails> =>
	api
		.post<API_ArchivePlatformPlanResponse>(
			`/platform-plans/${planId}/archive`,
			{},
			authHeader(token),
		)
		.then((res) => res.data.data);

export const useArchivePlatformPlan = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	return useMutationWithToast({
		mutationFn: ({ token, planId }: { token: string; planId: string }) =>
			archivePlatformPlan(token, planId),
		toastMessages: {
			loading: t("platformPlans.toasts.archiving"),
			success: t("platformPlans.toasts.archiveSuccess"),
			error: t("platformPlans.toasts.archiveError"),
		},
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({
				queryKey: [PLATFORM_PLANS_KEY, "detail", variables.planId],
			});
			queryClient.invalidateQueries({
				queryKey: [PLATFORM_PLANS_KEY, "list"],
			});
		},
	});
};

export const updatePlatformPlanEntitlements = (
	token: string,
	planId: string,
	body: API_UpdatePlatformPlanEntitlementsRequest,
): Promise<PlatformPlanDetails> =>
	api
		.put<API_UpdatePlatformPlanEntitlementsResponse>(
			`/platform-plans/${planId}/entitlements`,
			body,
			authHeader(token),
		)
		.then((res) => res.data.data);

export const useUpdatePlatformPlanEntitlements = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	return useMutationWithToast({
		mutationFn: ({
			token,
			planId,
			body,
		}: {
			token: string;
			planId: string;
			body: API_UpdatePlatformPlanEntitlementsRequest;
		}) => updatePlatformPlanEntitlements(token, planId, body),
		toastMessages: {
			loading: t("platformPlans.toasts.savingEntitlements"),
			success: t("platformPlans.toasts.saveEntitlementsSuccess"),
			error: t("platformPlans.toasts.saveEntitlementsError"),
		},
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({
				queryKey: [PLATFORM_PLANS_KEY, "detail", variables.planId],
			});
			queryClient.invalidateQueries({
				queryKey: [PLATFORM_PLANS_KEY, "list"],
			});
		},
	});
};
